// Bring records that landed ON DISK into a dirty in-memory board BEFORE that
// board is flushed.
//
// Why: the board tab persists whole boards through extension_records_sync_scope,
// which tombstones every record in the scope missing from the array it is
// handed. A personal-sync pull from another device writes new cards to disk; if
// the open board already has an unsaved local edit (a drag dropped while the
// re-read was deferred, an edit in the 400 ms debounce window), the old
// reconcileFromDisk flushed that stale snapshot FIRST and deleted the pulled
// cards. (Card 4f6be6c3, code review 24.09 2D §2.1, Scenario B.)
//
// The fix reuses the WS-frame path verbatim: the disk board is turned into
// records and patched in with patchBoardWithRecords, skipping every entity this
// tab still has an unsaved local edit for (skipIds — its own creates, edits
// and deletes). So a pulled card is added, a pulled field change to an
// untouched card is taken (subject to the same stale-updatedAt guard), and
// nothing local that is pending is overwritten or resurrected.

import { decomposeBoard, patchBoardWithRecords } from './boardsStore';

/** Disk board → the flat record shape patchBoardWithRecords expects. */
export function boardToFlatRecords(board) {
  if (!board) return [];
  return decomposeBoard(board)
    .filter((r) => r.record_type !== 'board')
    .map(({ data, ...envelope }) => ({ ...data, ...envelope }));
}

/**
 * @param {object} local - the dirty in-memory board about to be persisted
 * @param {object|null} disk - the same board as read from disk just now
 * @param {Set<string>} [skipIds] - entities with an unsaved local edit
 * @returns the rebased board, or `local` itself when the disk adds nothing
 */
export function rebaseDirtyBoardOntoDisk(local, disk, skipIds) {
  if (!local || !disk || disk.id !== local.id) return local;
  return patchBoardWithRecords(local, boardToFlatRecords(disk), { skipIds });
}

/**
 * Rebase every dirty board onto its disk copy, one read per board. The dirty
 * entry is looked up again AFTER each read, so an edit made while the read was
 * in flight is the version that gets rebased. A failed read leaves that board
 * as it is (the caller still flushes it — the pre-fix behaviour, never worse).
 *
 * @param {object} deps
 * @param {Map<string, object>} deps.dirty - board id → snapshot queued to persist
 * @param {(id: string) => (Set<string>|undefined)} deps.skipIdsFor
 * @param {(id: string, scopeKey: string) => Promise<object|null>} deps.readBoard
 * @param {(board: object) => string} deps.scopeKeyOf
 * @param {(id: string, next: object) => void} deps.apply - store the rebased board
 */
export async function rebaseDirtyBoards({ dirty, skipIdsFor, readBoard, scopeKeyOf, apply }) {
  for (const [id, queued] of [...dirty.entries()]) {
    let disk;
    try {
      disk = await readBoard(id, scopeKeyOf(queued)); // eslint-disable-line no-await-in-loop
    } catch (err) {
      console.warn(`[task-manager] disk read for rebase of board ${id} failed:`, err);
      continue;
    }
    const current = dirty.get(id);
    if (!current) continue; // flushed or deleted meanwhile
    const next = rebaseDirtyBoardOntoDisk(current, disk, skipIdsFor(id));
    if (next !== current) apply(id, next);
  }
}
